import React from "react";
import { Form, InputGroup } from "react-bootstrap";
import { FormikProps } from "formik";
import { SimulatorTextField } from "../fields/SimulatorTextField";
import { BessCsvFields } from "./BessCsvFields";
import { useBessProfiles } from "../hooks/useBessProfiles";
import { FormValues } from "../types";

const FormSwitch = (Form as any).Switch;

interface BatterySectionProps {
  formik: FormikProps<FormValues>;
  buttonSelected: string;
}

export const BatterySection = ({
  formik,
  buttonSelected,
}: BatterySectionProps) => {
  // Sem medição carregada a série horária é sintética: o backend a monta a
  // partir do mês de referência e do perfil de consumo.
  const sinteticoMode = formik.values.medicaoCsv === "";

  // Nas comparações espelhadas de bateria o uso de bateria é intrínseco ao
  // tipo, então o toggle fica travado ligado.
  const bateriaTravada =
    buttonSelected === "MLBESS" || buttonSelected === "APEBESS";

  const {
    profiles,
    loading: profilesLoading,
    error: profilesError,
    retry: retryProfiles,
  } = useBessProfiles(formik.values.isBateria && sinteticoMode);

  const handleBateriaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    if (!event.target.checked) {
      formik.setFieldValue("medicaoCsv", "");
      formik.setFieldValue("medicaoCsvFileName", "");
      formik.setFieldValue("pontoMedicaoBess", "");
      formik.setFieldValue("submercado", "");
      formik.setFieldValue("mesReferencia", "");
      formik.setFieldValue("perfilConsumo", "");
    }
  };

  return (
    <>
      <div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="simulacao-form-header">Dados de bateria</span>
        </div>

        <hr />
      </div>

      <div className="formLine">
        <Form.Group
          controlId="isBateria"
          style={{
            width: "100%",
            maxWidth: "375px",
            height: "80px",
            display: "flex",
            gap: "32px",
          }}
        >
          <Form.Label className="simulacao-form-title">
            Utiliza bateria?
          </Form.Label>
          <FormSwitch
            type="switch"
            name="isBateria"
            label=""
            value={formik.values.isBateria}
            onBlur={formik.handleBlur}
            onChange={handleBateriaChange}
            checked={formik.values.isBateria}
            disabled={bateriaTravada}
          />
        </Form.Group>
      </div>

      {formik.values.isBateria && (
        <div className="formLine">
          {sinteticoMode && (
            <>
              <Form.Group
                controlId="mesReferencia"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Mês de referência <span>*</span>
                </Form.Label>
                <Form.Control
                  type="month"
                  name="mesReferencia"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.mesReferencia}
                  isInvalid={
                    !!formik.touched.mesReferencia &&
                    !!formik.errors.mesReferencia
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.mesReferencia}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group
                controlId="perfilConsumo"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  // Altura mínima em vez de fixa: o aviso de falha ao carregar
                  // os perfis somado ao erro de validação ultrapassa os 80px e
                  // invadiria a linha de baixo da grid.
                  minHeight: "80px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Perfil de consumo <span>*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  name="perfilConsumo"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.perfilConsumo}
                  isInvalid={
                    !!formik.touched.perfilConsumo &&
                    !!formik.errors.perfilConsumo
                  }
                  disabled={profilesLoading}
                >
                  <option value="">
                    {profilesLoading ? "Carregando..." : "Selecione"}
                  </option>
                  {profiles.map((profile) => (
                    <option
                      key={profile.codigo}
                      value={profile.codigo}
                      title={profile.descricao}
                    >
                      {profile.label}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.perfilConsumo}
                </Form.Control.Feedback>
                {profilesError && (
                  <Form.Text className="text-danger">
                    {profilesError}{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={retryProfiles}
                    >
                      Tentar novamente
                    </button>
                  </Form.Text>
                )}
              </Form.Group>

              {/* <Form.Group
                controlId="isDescontoGarantido"
                style={{
                  width: "100%",
                  maxWidth: "375px",
                  height: "80px",
                  display: "flex",
                  gap: "32px",
                }}
              >
                <Form.Label className="simulacao-form-title">
                  Consumidor migrou com desconto na demanda?
                </Form.Label>
                <FormSwitch
                  type="switch"
                  name="isDescontoGarantido"
                  label=""
                  value={formik.values.isDescontoGarantido}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  checked={formik.values.isDescontoGarantido}
                />
              </Form.Group> */}
            </>
          )}

          <BessCsvFields formik={formik} />

          <Form.Group
            controlId="rteBateria"
            style={{
              width: "100%",
              maxWidth: "375px",
              height: "80px",
            }}
          >
            <Form.Label className="simulacao-form-title">
              RTE <span>*</span>
            </Form.Label>
            <InputGroup className="mb-3">
              <Form.Control
                name="rteBateria"
                placeholder="Porcentagem"
                onBlur={formik.handleBlur}
                value={formik.values.rteBateria}
                onChange={formik.handleChange}
                isInvalid={
                  !!formik.touched.rteBateria && !!formik.errors.rteBateria
                }
                required
                min={0}
                max={100}
              />
              <InputGroup.Text>%</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {formik.errors.rteBateria}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </div>
      )}

      {formik.values.isBateria && (
        <div className="formLine">
          <SimulatorTextField
            formik={formik}
            name="potenciaBateria"
            label={
              <>
                Potência da bateria <span>*</span>
              </>
            }
            placeholder="Em kW"
            unit="kW"
            required
          />

          <SimulatorTextField
            formik={formik}
            name="energiaMaximaArmazenada"
            label={
              <>
                Energia máxima armazenável <span>*</span>
              </>
            }
            placeholder="Em kWh"
            unit="kWh"
            required
          />

          <SimulatorTextField
            formik={formik}
            name="investimentoBateria"
            label={
              <>
                Investimento bateria <span>*</span>
              </>
            }
            placeholder="Em reais"
            prefix="R$"
            required={buttonSelected !== "ML"}
          />
        </div>
      )}
    </>
  );
};
